import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Grid, Stack, Typography } from '@mui/material';
import ProCrownButton from '../billing/ProCrownButton';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import BrandColorField from './BrandColorField';
import StoreBrandingPreview from './StoreBrandingPreview';
import StoreBrandingImagesPanel from './StoreBrandingImagesPanel';
import StoreService from '../../services/storeService';
import { useToast } from '../../context/ToastContext';
import { BRAND, BRAND_COLOR_ROLES, DEFAULT_BRAND_COLORS } from '../../theme/brandTokens';
import { buildBrandPalette, isValidBrandColor, normalizeBrandColors } from '../../theme/buildBrandPalette';
import { writeCachedBrandColors } from '../../theme/brandColorCache';

const EMPTY_VALUES = { primary: '', accent: '', text: '', background: '' };

const toFormValues = (colors) => ({ ...EMPTY_VALUES, ...(normalizeBrandColors(colors) || {}) });

const sameValues = (left, right) => BRAND_COLOR_ROLES.every((role) => (
  (left[role] || '').toUpperCase() === (right[role] || '').toUpperCase()
));

/** Store hub tab where a Pro store sets its four optional brand colours. */
const StoreBrandingPanel = ({
  storeId,
  store,
  canUpdate,
  allowsCustomBranding,
  onStoreChanged,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const [savedValues, setSavedValues] = useState(() => toFormValues(store?.brandColors));
  const [values, setValues] = useState(savedValues);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = toFormValues(store?.brandColors);
    setSavedValues(next);
    setValues(next);
  }, [store?.brandColors]);

  const editable = canUpdate && allowsCustomBranding;
  const hasInvalid = BRAND_COLOR_ROLES.some((role) => values[role] && !isValidBrandColor(values[role]));
  const isDirty = !sameValues(values, savedValues);
  const draftColors = useMemo(() => normalizeBrandColors(values), [values]);
  const palette = useMemo(() => buildBrandPalette(draftColors), [draftColors]);
  const requestedText = draftColors?.text || DEFAULT_BRAND_COLORS.text;
  const warnings = [
    palette.text !== requestedText ? t('stores.branding.warnings.textContrast') : null,
    palette.accentText !== palette.accent ? t('stores.branding.warnings.accentContrast') : null,
  ].filter(Boolean);

  const handleChange = (role) => (nextValue) => {
    setValues((current) => ({ ...current, [role]: nextValue }));
  };

  const handleSave = async () => {
    if (!editable || hasInvalid) return;
    try {
      setSaving(true);
      const result = await StoreService.updateBrandColors(storeId, draftColors);
      const saved = normalizeBrandColors(result);
      const next = toFormValues(saved);
      setSavedValues(next);
      setValues(next);
      writeCachedBrandColors(storeId, saved);
      showSuccess(t('stores.branding.saved'));
      onSaved?.(saved);
    } catch (error) {
      console.error('Error saving brand colors:', error);
      showError(t('stores.branding.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box id="store-branding-panel" sx={{ p: { xs: 2.5, sm: 3, md: 4 }, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          {t('stores.branding.title')}
        </Typography>
        {!allowsCustomBranding ? <ProCrownButton locked /> : null}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3, maxWidth: 640 }}>
        {t('stores.branding.description')}
      </Typography>

      {allowsCustomBranding && !canUpdate ? (
        <Alert severity="info" sx={{ mb: 3 }}>{t('stores.branding.readOnly')}</Alert>
      ) : null}

      <Grid container spacing={{ xs: 3, md: 4 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={3}>
            {BRAND_COLOR_ROLES.map((role) => (
              <BrandColorField
                key={role}
                id={`store-branding-${role}`}
                role={role}
                value={values[role]}
                defaultColor={DEFAULT_BRAND_COLORS[role]}
                onChange={handleChange(role)}
                disabled={!editable || saving}
              />
            ))}
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography component="h3" sx={{ fontWeight: 600, mb: 1 }}>
            {t('stores.branding.previewTitle')}
          </Typography>
          <StoreBrandingPreview
            colors={draftColors}
            storeName={store?.name}
            images={store?.images}
          />
          {warnings.map((warning) => (
            <Alert key={warning} severity="warning" sx={{ mt: 2 }}>{warning}</Alert>
          ))}
        </Grid>
      </Grid>

      {editable ? (
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button
            id="store-branding-reset"
            onClick={() => setValues(EMPTY_VALUES)}
            disabled={saving || sameValues(values, EMPTY_VALUES)}
            sx={{ color: BRAND.textMuted }}
          >
            {t('stores.branding.resetAll')}
          </Button>
          <PrimaryActionButton
            id="store-branding-save"
            onClick={handleSave}
            disabled={saving || !isDirty || hasInvalid}
          >
            {saving ? t('common.saving') : t('common.save')}
          </PrimaryActionButton>
        </Stack>
      ) : null}

      <StoreBrandingImagesPanel
        storeId={storeId}
        store={store}
        allowsCustomBranding={allowsCustomBranding}
        onStoreChanged={onStoreChanged}
      />
    </Box>
  );
};

export default StoreBrandingPanel;
